import { NetworkType } from 'abis/set/sets'
import { CurrencyMetadata } from 'indices-list'
import { Address, zeroAddress } from 'viem'
import { defaultPriceRequest, PriceRequest } from './prices/model'
import { defaultCurrency, defaultQuoteParams, QuoteParams } from './zero-ex-requests/quotes/model'
import { defaultPriceParams, PriceParams } from './zero-ex-requests/output/model'
import { AnatomyWithExtra, defaultIndexAnatomy } from './anatomy'

// INFO: OVERVIEW PAGE QUERIES
export const QUERY_KEY_ALL_DAILY_STATS = (): { foreground: [string]; background: [string] } => ({
  foreground: ['product-all-daily-stats-foreground'],
  background: ['product-all-daily-stats-background'],
})

export const QUERY_KEY_ALL_PRODUCT_INFO = (): { foreground: [string]; background: [string] } => ({
  foreground: ['all-products-info-foreground'],
  background: ['all-products-info--background'],
})

// INFO: PRODUCT PAGE QUERIES

export const QUERY_KEY_APY_INFO = (
  a1?: Address, //productAddress
  a2?: NetworkType, //chainId
): { foreground: [string, Address, NetworkType]; background: [string, Address, NetworkType] } => {
  return {
    foreground: ['apy-info-foreground', a1 ?? zeroAddress, a2 ?? NetworkType.Mainnet],
    background: ['apy-info-background', a1 ?? zeroAddress, a2 ?? NetworkType.Mainnet],
  }
}

export const QUERY_KEY_PRODUCT_INFO = (
  a1?: Address, //productAddress
  a2?: NetworkType, //chainId
): { foreground: [string, Address, NetworkType]; background: [string, Address, NetworkType] } => {
  return {
    foreground: ['product-info-foreground', a1 ?? zeroAddress, a2 ?? NetworkType.Mainnet],
    background: ['product-info-background', a1 ?? zeroAddress, a2 ?? NetworkType.Mainnet],
  }
}

export const QUERY_KEY_DAILY_STATS = (
  a1?: Address, //productAddress
  a2?: NetworkType, //chainId
): { foreground: [string, Address, NetworkType]; background: [string, Address, NetworkType] } => {
  return {
    foreground: ['product-daily-stats-foreground', a1 ?? zeroAddress, a2 ?? NetworkType.Mainnet],
    background: ['product-daily-stats-background', a1 ?? zeroAddress, a2 ?? NetworkType.Mainnet],
  }
}

// INFO: STAKING PAGE QUERIES

export const QUERY_STAKING_INFO = (
  a1?: Address, //productAddress
): { foreground: [string, Address]; background: [string, Address] } => {
  return {
    foreground: ['staking-info-foreground', a1 ?? zeroAddress],
    background: ['staking-info-background', a1 ?? zeroAddress],
  }
}

export const QUERY_USER_STAKING_INFO = (
  a1?: Address, //productAddress
  a2?: Address, //userAddress
): { foreground: [string, Address, Address]; background: [string, Address, Address] } => {
  return {
    foreground: ['staking-user-info-foreground', a1 ?? zeroAddress, a2 ?? zeroAddress],
    background: ['staking-user-info-background', a1 ?? zeroAddress, a2 ?? zeroAddress],
  }
}

// INFO: NAVIGATION & PORTFOLIO QUERIES

export const QUERY_KEY_USER_HISTORIES = (
  a1?: Address, //userAddress
): { foreground: [string, Address]; background: [string, Address] } => {
  return {
    foreground: ['users-history-foreground', a1 ?? zeroAddress],
    background: ['users-history-background', a1 ?? zeroAddress],
  }
}

// INFO: INVESTMENT COMPONENT QUERIES

export const QUERY_KEY_OUTPUT_AMOUNT = (
  a1?: PriceParams, //requestParams
  a2?: AnatomyWithExtra, //indexAnatomy
): {
  foreground: [string, PriceParams, AnatomyWithExtra]
  background: [string, PriceParams, AnatomyWithExtra]
} => {
  return {
    foreground: ['output-amount-foreground', a1 ?? defaultPriceParams, a2 ?? defaultIndexAnatomy],
    background: ['output-amount-background', a1 ?? defaultPriceParams, a2 ?? defaultIndexAnatomy],
  }
}

export const QUERY_KEY_QUOTES = (
  a1?: QuoteParams, //quoteParams
): {
  foreground: [string, QuoteParams]
  background: [string, QuoteParams]
} => {
  return {
    foreground: ['quotes-foreground', a1 ?? defaultQuoteParams],
    background: ['quotes-background', a1 ?? defaultQuoteParams],
  }
  //INFO: temporary not using
}

export const QUERY_KEY_PRICE = (
  a1?: PriceRequest, //requestParams
): {
  foreground: [string, PriceRequest]
  background: [string, PriceRequest]
} => {
  const key = {
    foreground: ['price-foreground', a1 ?? defaultPriceRequest] as [string, PriceRequest],
    background: ['price-background', a1 ?? defaultPriceRequest] as [string, PriceRequest],
  }
  return key
}

export const QUERY_KEY_USER_BALANCE = (
  a1?: Address, //userAddress
  a2?: CurrencyMetadata, //currency
): {
  foreground: [string, Address, CurrencyMetadata]
  background: [string, Address, CurrencyMetadata]
} => {
  return {
    foreground: ['asset-balance-foreground', a1 ?? zeroAddress, a2 ?? defaultCurrency],
    background: ['asset-balance-background', a1 ?? zeroAddress, a2 ?? defaultCurrency],
  }
}

export const QUERY_ESCROW_STATUS = (
  a1?: Address, //userAddress
  a2?: AnatomyWithExtra, //indexAnatomy
): {
  foreground: [string, Address, AnatomyWithExtra]
  background: [string, Address, AnatomyWithExtra]
} => {
  return {
    foreground: ['escrow-status-foreground', a1 ?? zeroAddress, a2 ?? defaultIndexAnatomy],
    background: ['escrow-status-background', a1 ?? zeroAddress, a2 ?? defaultIndexAnatomy],
  }
}

export const QUERY_INDEX_ANATOMY = (
  a1?: Address, //indexAddress
): {
  foreground: [string, Address]
  background: [string, Address]
} => {
  return {
    foreground: ['index-anatomy-foreground', a1 ?? zeroAddress],
    background: ['index-anatomy-background', a1 ?? zeroAddress],
  }
}

export const QUERY_REBALANCE_STATUS = (
  a1?: Address, //indexAddress
): {
  foreground: [string, Address]
  background: [string, Address]
} => {
  return {
    foreground: ['rebalance-status-foreground', a1 ?? zeroAddress],
    background: ['rebalance-status-background', a1 ?? zeroAddress],
  }
}

export const QUERY_IS_USER_CONTRACT_STATUS = (
  a1?: Address, //userAddress
): {
  foreground: [string, Address]
  background: [string, Address]
} => {
  return {
    foreground: ['is-user-contract-status-foreground', a1 ?? zeroAddress],
    background: ['is-user-contract-status-background', a1 ?? zeroAddress],
  }
}

export const QUERY_KEY_USER_ALLOWANCE = (
  a1?: Address, //userAddress
  a2?: CurrencyMetadata, //currency
  a3?: Address, //takerAddress
): {
  foreground: [string, Address, CurrencyMetadata, Address]
  background: [string, Address, CurrencyMetadata, Address]
} => {
  return {
    foreground: [
      'allowance-foreground',
      a1 ?? zeroAddress,
      a2 ?? defaultCurrency,
      a3 ?? zeroAddress,
    ],
    background: [
      'allowance-background',
      a1 ?? zeroAddress,
      a2 ?? defaultCurrency,
      a3 ?? zeroAddress,
    ],
  }
}
